import { useEffect } from "react";
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from "react-router";
import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import { isChunkLoadError, reloadOnceForStaleChunk } from "@/lib/stale-chunk-reload";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";

function getErrorDetails(error: unknown): {
    heading: string;
    title: string;
    description: string;
    detail?: string;
} {
    if (isRouteErrorResponse(error)) {
        if (error.status === 404) {
            return {
                heading: "404",
                title: "Page Not Found",
                description: "Sorry, the page you're looking for doesn't exist or has been moved.",
            };
        }

        if (error.status === 401 || error.status === 403) {
            return {
                heading: String(error.status),
                title: "Access Denied",
                description: "You don't have permission to view this page. Try logging in again.",
            };
        }

        return {
            heading: String(error.status),
            title: error.statusText || "Something Went Wrong",
            description: "The server responded with an error while loading this page.",
            detail: typeof error.data === "string" ? error.data : undefined,
        };
    }

    if (isChunkLoadError(error)) {
        return {
            heading: "Oops",
            title: "OneVault Has Been Updated",
            description: "This page couldn't load because a newer version has been released. Reload to continue.",
            detail: error instanceof Error ? error.message : undefined,
        };
    }

    if (error instanceof Error) {
        return {
            heading: "Oops",
            title: "Something Went Wrong",
            description: "An unexpected error occurred while loading this page.",
            detail: error.message,
        };
    }

    return {
        heading: "Oops",
        title: "Something Went Wrong",
        description: "An unexpected error occurred while loading this page.",
    };
}

export default function RouteErrorBoundary() {
    const error = useRouteError();
    const navigate = useNavigate();
    const location = useLocation();
    const { heading, title, description, detail } = getErrorDetails(error);

    // Errors caught by a React error boundary never reach window.onerror, so Dash0 only
    // learns about them if we report them. 404s are routine navigation, not failures.
    useEffect(() => {
        if (isRouteErrorResponse(error) && error.status === 404) {
            return;
        }

        // A stale tab after a deploy: reload once to pick up the new build rather than
        // reporting it. If that reload has already happened, fall through and report it.
        if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
            return;
        }

        logger.error("Route failed to render", error, {
            "http.response.status_code": isRouteErrorResponse(error) ? error.status : undefined,
            "url.path": location.pathname,
        });
    }, [error, location.pathname]);

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md text-center">
                <CardHeader>
                    <div className="mx-auto mb-4 text-6xl font-bold text-muted-foreground">
                        {heading}
                    </div>
                    <CardTitle className="text-2xl">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                {detail && (
                    <CardContent>
                        <p className="break-words rounded-md bg-muted p-3 text-left text-sm text-muted-foreground">
                            {detail}
                        </p>
                    </CardContent>
                )}
                <CardFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
                    <Button variant="default" onClick={() => navigate(0)}>
                        Try Again
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/files")}>
                        Go to Files
                    </Button>
                    <Button variant="outline" onClick={() => navigate(-1)}>
                        Go Back
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
